import { useIntl } from 'react-intl';
import { DoohAudienceProfilingStatus } from 'sonata-static-storage/lib/schemas/doohReport/doohAudienceProfiling.schema';

import { AudienceInsights } from '@/components/common/AudienceInsights/AudienceInsights';
import { AudienceInsightsSkeleton } from '@/components/common/AudienceInsights/AudienceInsightsSkeleton';
import SectionHeader from '@/components/doohReport/common/SectionHeader/SectionHeader';

import type { DoohAudienceProfilingOutputData } from 'sonata-static-storage/lib/schemas/doohReport/doohAudienceProfiling.schema';
import type { AudienceInsightsLabels } from '@/components/common/AudienceInsights/AudienceInsights';

import './audienceProfiling.scss';

type Props = {
  data: DoohAudienceProfilingOutputData | null;
  isLoading: boolean;
  sectionNumber: string;
  status: DoohAudienceProfilingStatus | undefined;
};

/**
 * Audience profiling section of the DOOH report.
 *
 * Same card as the footfall report's buyer insights, but keyed on the line item:
 * there is no POI, so the profile is derived from the campaign itself. The
 * profile is generated asynchronously by an AI agent; `DoohReport` owns the fetch
 * and the socket subscription (the same payload also feeds the summary card) and
 * passes down the resolved data and status.
 */
export default function AudienceProfiling({ data, isLoading, sectionNumber, status }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const header = (
    <SectionHeader
      description={formatMessage({
        defaultMessage: 'Who we actually reached.',
        id: 'doohReport.audienceProfiling.description'
      })}
      number={sectionNumber}
      title={formatMessage({
        defaultMessage: 'Audience Profiling.',
        id: 'doohReport.audienceProfiling.title'
      })}
      tooltip={formatMessage({
        defaultMessage:
          'The real profile of the people impacted — built from bidstream signals and mobile SDK data, framed against the campaign target.',
        id: 'doohReport.audienceProfiling.tooltip'
      })}
    />
  );

  if (isLoading || status === DoohAudienceProfilingStatus.processing) {
    return (
      <section className="audienceProfiling">
        {header}
        <AudienceInsightsSkeleton />
      </section>
    );
  }

  // The section is dropped rather than shown empty: a failed or absent profile
  // has nothing meaningful to render, and the rest of the report stands alone.
  if (status === DoohAudienceProfilingStatus.error || !data) {
    return null;
  }

  const labels: AudienceInsightsLabels = {
    affinitiesHeader: formatMessage({
      defaultMessage: "Audience's Lifestyle & Affinities",
      id: 'doohReport.audienceProfiling.affinities.header'
    }),
    avgAge: formatMessage({ defaultMessage: 'AVG AGE', id: 'doohReport.audienceProfiling.avgAge' }),
    bottomBrands: formatMessage({
      defaultMessage: 'LOW BRAND AFFINITIES',
      id: 'doohReport.audienceProfiling.bottomBrands'
    }),
    coreNeeds: formatMessage({ defaultMessage: 'CORE NEEDS', id: 'doohReport.audienceProfiling.coreNeeds' }),
    genderLabel: formatMessage({ defaultMessage: 'GENDER', id: 'doohReport.audienceProfiling.gender.label' }),
    header: formatMessage({ defaultMessage: 'Target Audience', id: 'doohReport.audienceProfiling.header' }),
    highAffinity: formatMessage({ defaultMessage: 'High', id: 'doohReport.audienceProfiling.affinity.high' }),
    interests: formatMessage({ defaultMessage: 'INTERESTS', id: 'doohReport.audienceProfiling.interests' }),
    men: formatMessage({ defaultMessage: 'Men', id: 'doohReport.audienceProfiling.gender.men' }),
    primaryValues: formatMessage({
      defaultMessage: 'PRIMARY VALUES',
      id: 'doohReport.audienceProfiling.primaryValues'
    }),
    statusLabel: formatMessage({ defaultMessage: 'STATUS', id: 'doohReport.audienceProfiling.status.label' }),
    subHeader: formatMessage({
      defaultMessage: 'The average person reached by this campaign',
      id: 'doohReport.audienceProfiling.subHeader'
    }),
    targetPersona: formatMessage({
      defaultMessage: 'TARGET PERSONA',
      id: 'doohReport.audienceProfiling.targetPersona.label'
    }),
    topBrands: formatMessage({
      defaultMessage: 'TOP BRAND AFFINITIES',
      id: 'doohReport.audienceProfiling.topBrands'
    }),
    topCategories: formatMessage({
      defaultMessage: 'TOP CATEGORIES',
      id: 'doohReport.audienceProfiling.topCategories'
    }),
    women: formatMessage({ defaultMessage: 'Women', id: 'doohReport.audienceProfiling.gender.women' }),
    yearsOld: formatMessage({ defaultMessage: 'yrs', id: 'doohReport.audienceProfiling.yearsOld' })
  };

  return (
    <section className="audienceProfiling">
      {header}
      <AudienceInsights data={data} labels={labels} />
    </section>
  );
}
